import React, { useState } from 'react';
import {
  FormattedMessage,
  useIntl,
} from 'react-intl';
import {
  Modal,
  Textarea,
  Button,
  Label,
} from '@printdeal/ui-library';
import { useFormik } from 'formik';
import { QuoteLineItem } from '../../../../types/quotes/quoteLineItems';
import LineItem from '../../../../types/cart/LineItem';
// @ts-expect-error
import { getLocalized } from '../../../../helpers/getLocalisedData';
import { dispatchDataLayerEventOnQuoteReRequest } from '../../../../helpers/dataLayerHelpers/quoteDataLayerHelper';
import { ServiceFactory } from '../../../../services/ServiceFactory';

interface ExtendQuoteModalProp {
  quoteId: string;
  quoteLineItemId: string | string[];
  closeModal: (status: boolean) => void;
  quoteLineItems?: QuoteLineItem[];
  orderLineItem?: LineItem;
}

interface FormInitialValues {
  [key: string] : string;
}

const ExtendQuoteModal = ({
  quoteId,
  quoteLineItemId,
  closeModal,
  quoteLineItems,
  orderLineItem,
}: ExtendQuoteModalProp) => {
  const quotesApi = ServiceFactory.createQuotesService();
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();
  const { locale } = intl;

  const initialValues: FormInitialValues = {};

  if (Array.isArray(quoteLineItemId)) {
    quoteLineItemId.forEach((quoteLineItem) => {
      if (quoteLineItem !== 'select-all') {
        initialValues[quoteLineItem] = '';
      }
    });
  } else {
    initialValues.extendQuoteAdditionalWishes = '';
  }

  const extendQuoteFormik = useFormik({
    initialValues: { ...initialValues },
    onSubmit: async (values) => {
      setIsLoading(true);
      const quoteLineItemIds = [];
      if (Array.isArray(quoteLineItemId)) {
        quoteLineItemId.forEach((quoteLineItem) => {
          if (quoteLineItem !== 'select-all') {
            quoteLineItemIds.push({
              id: quoteLineItem,
              additionalWishes: values[quoteLineItem],
            });
          }
        });
      } else {
        quoteLineItemIds.push({
          id: quoteLineItemId,
          additionalWishes: values.extendQuoteAdditionalWishes,
        });
      }
      quotesApi?.createQuote({
        quoteLineItems: [],
        quoteId,
        quoteLineItemIds,
      }).then(() => {
        dispatchDataLayerEventOnQuoteReRequest();
        setIsLoading(false);
        closeModal(true);
      });
    },
  });

  const {
    handleSubmit,
    isSubmitting,
    getFieldProps,
    handleChange,
    values,
  } = extendQuoteFormik;

  const formGroup = [];

  if (Array.isArray(quoteLineItemId)) {
    quoteLineItems?.forEach((quoteLineItem) => {
      if (quoteLineItemId.includes(quoteLineItem.lineItemId)) {
        const { merchandisingProductName, lineItemId, productDescription } = quoteLineItem;
        formGroup.push(
          <fieldset key={lineItemId} className="pd-my-4">
            <Label>
              {getLocalized(locale, merchandisingProductName)}: <span className="pd-text-dim-500">{getLocalized(locale, productDescription)}</span>
            </Label>
            <Textarea
              id={lineItemId}
              {...getFieldProps(lineItemId)}
              className="pd-mt-2 pd-w-full"
            />
          </fieldset>,
        );
      }
    });
  } else {
    const localizedProductName = orderLineItem?.custom?.fields?.localizedProductName;
    const productDescription = orderLineItem?.custom?.fields?.productDescription;
    formGroup.push(
      <fieldset>
        <Label className="pd-mb-4">
          {getLocalized(locale, localizedProductName)}: <span className="pd-text-dim-500">{getLocalized(locale, productDescription)}</span>
        </Label>
        <Textarea
          id="extendQuoteAdditionalWishes"
          {...getFieldProps('extendQuoteAdditionalWishes')}
          onChange={handleChange}
          value={values.extendQuoteAdditionalWishes || ''}
          className="pd-mt-2 pd-w-full"
        />
      </fieldset>,
    );
  }

  return (
    <Modal
      isOpen
      onClose={() => closeModal(false)}
      title={(
        <FormattedMessage
          id="lineItems.list.extendQuote.title"
          defaultMessage="Extend Quote"
        />
      )}
      size="lg"
      dismissLabel={intl.formatMessage({ id: 'general.alert.dismiss', defaultMessage: 'Dismiss' })}
    >
      <Modal.Content>
        <form key="extendQuote-form" id="extendQuote-form" onSubmit={handleSubmit}>
          <div className="pd-mb-4 pd-font-bold">
            <FormattedMessage
              id="lineItems.list.extendQuote.body.title.additionalWishes"
              defaultMessage="Additional wishes if you like to add any"
            />
          </div>
          {formGroup}
        </form>
      </Modal.Content>
      <Modal.Footer className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2">
        <Button
          size="md"
          disabled={isLoading}
          isLoading={isLoading}
          type="submit"
          form="extendQuote-form"
        >
          <FormattedMessage
            id="lineItems.list.extendQuote.submit"
            defaultMessage="Submit"
          />
        </Button>
        <Button
          variant="secondary"
          onClick={() => closeModal(false)}
          disabled={isLoading || isSubmitting}
        >
          <FormattedMessage
            id="lineItems.list.extendQuote.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExtendQuoteModal;
